import db from '../db/db'
import Database from '../types/Database'
import {
  generateDateRange,
  getMinimumDate,
  calculateBookingDayRange
} from '../utils/dateRangeHelpers'
import { formatDateToString } from '../utils/FormatHelpers'

import BookingService from './BookingService'
import ErrorHandler from './ErrorHandler'

/**
 * Service for handling calendar-related business logic
 * Extracted from state.ts to improve separation of concerns
 */
export class CalendarService {
  /**
   * Find existing bookings for a date range and offices
   */
  static async findExistingBookings(
    selectedFromDate: Date,
    number: number,
    unit: 'Once off' | 'Same days every week',
    myOffices: Database.Office[]
  ): Promise<Database.Booking[]> {
    try {
      const dayRange = calculateBookingDayRange(unit, number)

      // To calculate the no. of days between two dates
      if (dayRange < 0) return []

      const dateRange: Date[] = generateDateRange(
        selectedFromDate,
        unit,
        number
      )
      const minDate = getMinimumDate(dateRange)

      if (myOffices.length <= 0) {
        return []
      }

      ErrorHandler.logDebug(
        'Finding existing bookings',
        {
          selectedFromDate,
          number,
          unit,
          officeCount: myOffices.length
        },
        { component: 'CalendarService', action: 'findExistingBookings' }
      )

      const bookings = await db.query({
        collection: 'booking',
        where: [
          ['forDate', '>=', minDate],
          ['oid', 'in', myOffices.map(x => x.oid)]
        ]
      })

      ErrorHandler.logDebug(
        'Found existing bookings',
        {
          bookingCount: bookings.length
        },
        { component: 'CalendarService', action: 'findExistingBookings' }
      )

      return bookings
    } catch (error) {
      ErrorHandler.logNetworkError('Error finding existing bookings', error, {
        component: 'CalendarService',
        action: 'findExistingBookings'
      })
      throw new Error(`Failed to find existing bookings: ${error}`)
    }
  }

  /**
   * Get bookings for a specific date range
   */
  static async getBookingsForDateRange(
    startDate: Date,
    numDays: number,
    offices: Database.Office[]
  ): Promise<Database.Booking[]> {
    try {
      if (offices.length === 0) {
        return []
      }

      const endDate = new Date(startDate)
      endDate.setDate(startDate.getDate() + numDays)

      const bookings = await db.query({
        collection: 'booking',
        where: [
          ['forDate', '>=', startDate],
          ['forDate', '<', endDate],
          ['oid', 'in', offices.map(x => x.oid)]
        ]
      })

      return bookings
    } catch (error) {
      ErrorHandler.logNetworkError(
        'Error getting bookings for date range',
        error,
        {
          component: 'CalendarService',
          action: 'getBookingsForDateRange'
        }
      )
      throw new Error(`Failed to get bookings for date range: ${error}`)
    }
  }

  /**
   * Get notifications for a specific date
   */
  static async getNotificationsForDate(
    date: string,
    offices: Database.Office[]
  ): Promise<Database.OfficeNotification[]> {
    try {
      if (offices.length === 0) {
        return []
      }

      const notifications = await db.query({
        collection: 'officeNotifications',
        where: [
          ['date', '==', date],
          ['officeId', 'in', offices.map(x => x.oid)]
        ]
      })

      return notifications
    } catch (error) {
      ErrorHandler.logNetworkError(
        'Error getting notifications for date',
        error,
        {
          component: 'CalendarService',
          action: 'getNotificationsForDate'
        }
      )
      throw new Error(`Failed to get notifications for date: ${error}`)
    }
  }

  /**
   * Delete a booking for a specific date and user
   * Delegates to BookingService to ensure analytics tracking
   */
  static async deleteBookingForDate(date: Date, userId: string): Promise<void> {
    try {
      // Delegate to BookingService for consistent analytics tracking
      await BookingService.deleteBookingForDate(date, userId)
    } catch (error) {
      ErrorHandler.logNetworkError('Error deleting booking for date', error, {
        component: 'CalendarService',
        action: 'deleteBookingForDate'
      })
      throw new Error(`Failed to delete booking for date: ${error}`)
    }
  }

  /**
   * Get user's bookings
   */
  static async getUserBookings(userId: string): Promise<Database.Booking[]> {
    try {
      const bookings = await db.query({
        collection: 'booking',
        where: ['uid', '==', userId]
      })

      return bookings
    } catch (error) {
      ErrorHandler.logNetworkError('Error getting user bookings', error, {
        component: 'CalendarService',
        action: 'getUserBookings'
      })
      throw new Error(`Failed to get user bookings: ${error}`)
    }
  }

  /**
   * Refresh bookings for a new date selection
   */
  static async refreshBookingsForDate(
    newDate: Date,
    selectedRecurAmount: number,
    selectedRecurUnit: 'Once off' | 'Same days every week',
    offices: Database.Office[]
  ): Promise<{
    bookings: Database.Booking[]
    selectedFromDate: string
  }> {
    try {
      const bookings = await this.findExistingBookings(
        newDate,
        selectedRecurAmount,
        selectedRecurUnit,
        offices
      )

      return {
        bookings,
        selectedFromDate: formatDateToString(newDate)
      }
    } catch (error) {
      ErrorHandler.logNetworkError(
        'Error refreshing bookings for date',
        error,
        {
          component: 'CalendarService',
          action: 'refreshBookingsForDate'
        }
      )
      throw new Error(`Failed to refresh bookings for date: ${error}`)
    }
  }
}

export default CalendarService
